/**
 * KEWELO Platinum v1 — Rich Media PDP behaviour.
 *
 * The interaction model of the frozen UX2-R3 prototype (`01-prototype/app.js`), ported to the
 * `sections/pv1-*.liquid` markup. One version state is shared by the buy box, the comparison
 * table and the mobile comparison cards; one coverage state; one quantity. What those states
 * drive is now real:
 *
 *   - the version writes the variant id into Horizon's product form and the `?variant=` URL;
 *   - the coverage choice is read at submit time by `warranty-cart.js`, which adds a paid plan
 *     as its own cart line paired to the bike — nothing in this file touches the cart;
 *   - totals multiply by quantity, because the cart will.
 *
 * Departures from the prototype, none of them visible: the hidden sticky bar is `inert`;
 * choosing a version from the comparison scrolls the thumbnail strip, never the page; films do
 * not start under `prefers-reduced-motion`; scroll tracking listens in capture phase so it works
 * whether the window or Horizon's `.page-wrapper` is the scroller.
 */

/** Y5 creator card ("Under $1000") is shown only while the selected selling price is ≤ $1,000. */
const PRICE_GUARD_MAX = 100000;
const MAX_QUANTITY = 9;
const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * @param {string} name
 * @returns {HTMLElement | null}
 */
function hook(name) {
  const element = document.querySelector(`[data-pv1="${name}"]`);
  return element instanceof HTMLElement ? element : null;
}

/**
 * @param {string} selector
 * @param {ParentNode} [scope]
 * @returns {HTMLElement[]}
 */
function all(selector, scope = document) {
  return /** @type {HTMLElement[]} */ (Array.from(scope.querySelectorAll(selector)));
}

/**
 * Marks an element as wired so a theme-editor section reload never doubles its listeners.
 * @param {HTMLElement} element
 * @returns {boolean} true the first time only
 */
function claim(element) {
  if (element.dataset.pv1Ready) return false;
  element.dataset.pv1Ready = 'true';
  return true;
}

/**
 * A `role="radio"` group: click, Space or Enter selects; arrow keys move, select and wrap.
 * @param {HTMLElement} group
 * @param {(radio: HTMLElement) => void} select
 */
function wireRadioGroup(group, select) {
  const radios = all('[role="radio"]', group);
  radios.forEach((radio, index) => {
    radio.addEventListener('click', () => select(radio));
    radio.addEventListener('keydown', (event) => {
      const { key } = event;
      if (key === ' ' || key === 'Enter') {
        event.preventDefault();
        select(radio);
      } else if (key.startsWith('Arrow')) {
        event.preventDefault();
        const step = key === 'ArrowRight' || key === 'ArrowDown' ? 1 : -1;
        const next = radios[(index + step + radios.length) % radios.length];
        if (!next) return;
        select(next);
        next.focus();
      }
    });
  });
}

function initBuyBox() {
  const buy = document.querySelector('[data-pv1-buy]');
  const source = document.querySelector('[data-pv1-data]');
  if (!(buy instanceof HTMLElement) || !source || !claim(buy)) return;

  /**
   * @type {{
   *   currency: string, initial: string, soldOut: string, atcLabel: string, saveLabel: string,
   *   variants: Record<string, {id: number, price: number, compare: number, available: boolean, range: string, img: number}>,
   *   images: Array<{src: string, alt: string, cap: string, cfg: string, photo: boolean}>
   * }}
   */
  const data = JSON.parse(source.textContent || '{}');
  const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: data.currency || 'USD' });
  /** @param {number} cents */
  const money = (cents) => currency.format(cents / 100);
  /** @param {number} cents */
  const wholeMoney = (cents) => money(cents).replace(/\.00$/, '');

  const state = { version: data.initial, image: 0, quantity: 1, cover: 0, coverLabel: '' };
  const variantInput = buy.querySelector('form input[name="id"]');
  const quantityInput = hook('qtyinput');

  /**
   * @param {string} name
   * @param {string} text
   */
  const setText = (name, text) => {
    const element = hook(name);
    if (element) element.textContent = text;
  };

  // ---- Gallery ------------------------------------------------------------------------------
  const images = data.images || [];
  const mainImage = hook('mainimg');
  const thumbStrip = buy.querySelector('.thumbs');
  const thumbs = all('.thumbs button', buy);
  state.image = Number(mainImage?.dataset.index) || 0;

  /** @param {number} index */
  function showImage(index) {
    if (!images.length || !(mainImage instanceof HTMLImageElement)) return;
    state.image = (index + images.length) % images.length;
    const image = images[state.image];
    if (!image) return;

    if (mainImage.dataset.index !== String(state.image)) {
      // Keep responsive selection across a swap: without this the gallery would drop back to the
      // widest file for every image but the first one.
      if (image.srcset) {
        mainImage.srcset = image.srcset;
        if (image.sizes) mainImage.sizes = image.sizes;
      } else {
        mainImage.removeAttribute('srcset');
        mainImage.removeAttribute('sizes');
      }
      mainImage.src = image.src;
      mainImage.alt = image.alt;
      mainImage.className = image.photo ? 'photo' : '';
      mainImage.dataset.index = String(state.image);
    }

    thumbs.forEach((thumb, i) =>
      i === state.image ? thumb.setAttribute('aria-current', 'true') : thumb.removeAttribute('aria-current')
    );

    let caption = image.cap;
    if ((image.cfg === 'single' || image.cfg === 'dual') && image.cfg !== state.version) {
      caption += ` · not the selected ${state.version}-battery model`;
    } else if (image.cfg === 'unconfirmed') {
      caption += ` · selected: ${state.version}-battery model`;
    }
    setText('cap', caption);
    setText('capn', `${state.image + 1} / ${images.length}`);

    // Keep the active thumbnail in view by scrolling the strip itself. The prototype used
    // scrollIntoView, which also scrolled the page back to the gallery whenever a version was
    // chosen from the comparison table further down.
    const thumb = thumbs[state.image];
    if (thumb && thumbStrip instanceof HTMLElement) {
      const strip = thumbStrip.getBoundingClientRect();
      const box = thumb.getBoundingClientRect();
      if (box.left < strip.left) thumbStrip.scrollLeft -= strip.left - box.left;
      else if (box.right > strip.right) thumbStrip.scrollLeft += box.right - strip.right;
    }
  }

  thumbs.forEach((thumb) => thumb.addEventListener('click', () => showImage(Number(thumb.dataset.i) || 0)));
  hook('gprev')?.addEventListener('click', () => showImage(state.image - 1));
  hook('gnext')?.addEventListener('click', () => showImage(state.image + 1));

  // ---- Price line, totals, summaries --------------------------------------------------------
  function paint() {
    const variant = data.variants[state.version];
    if (!variant) return;

    setText('price', money(variant.price));
    const was = hook('was');
    if (was) {
      const hasReference = variant.compare > variant.price;
      was.hidden = !hasReference;
      if (hasReference) {
        setText('cmpat', money(variant.compare));
        setText('save', `${data.saveLabel} ${wholeMoney(variant.compare - variant.price)}`);
      }
    }
    setText('rsel', `${variant.range}.`);

    const total = (variant.price + state.cover) * state.quantity;
    setText('atcp', money(total));
    setText('satcp', money(total));
    for (const name of ['atc', 'satc']) {
      const button = hook(name);
      if (button instanceof HTMLButtonElement) button.disabled = !variant.available;
      setText(`${name}label`, variant.available ? data.atcLabel : data.soldOut);
    }

    const summary = hook('wsum');
    const bar = hook('ssum');
    if (state.cover > 0) {
      const bike = money(variant.price);
      const coverage = money(state.cover);
      if (summary) {
        summary.textContent =
          state.quantity > 1
            ? `Includes ${state.coverLabel} (+${coverage} each). ${state.quantity} × (bike ${bike} + coverage ${coverage}).`
            : `Includes ${state.coverLabel} (+${coverage}). Bike ${bike} + coverage ${coverage}.`;
        summary.hidden = false;
      }
      if (bar) {
        const lead = document.createElement('span');
        lead.className = 'l';
        lead.textContent = `Bike ${bike} + `;
        bar.replaceChildren(lead, `coverage ${coverage}`);
        bar.hidden = false;
      }
    } else {
      if (summary) {
        summary.hidden = true;
        summary.textContent = '';
      }
      if (bar) {
        bar.hidden = true;
        bar.replaceChildren();
      }
    }
  }

  // ---- Version ------------------------------------------------------------------------------
  const versionGroups = all('[data-pv1-group="version"], [data-pv1-group="compare"], [data-pv1-group="compare-m"]');

  /**
   * @param {string} key
   * @param {{ user?: boolean }} [options]
   */
  function setVersion(key, { user = false } = {}) {
    const variant = data.variants[key];
    if (!variant) return;
    state.version = key;

    for (const group of versionGroups) {
      for (const radio of all('[role="radio"]', group)) {
        const on = radio.dataset.v === key;
        radio.setAttribute('aria-checked', String(on));
        radio.tabIndex = on ? 0 : -1;
        const label = radio.querySelector('.sel');
        if (label) label.textContent = on ? group.dataset.selectedLabel || 'Selected' : group.dataset.selectLabel || 'Select this version';
      }
    }
    for (const cell of all('[data-pv1-ctable] tbody td')) cell.classList.toggle('on', cell.dataset.v === key);
    for (const card of all('[data-price-guard]')) card.hidden = variant.price > PRICE_GUARD_MAX;

    if (variantInput instanceof HTMLInputElement) variantInput.value = String(variant.id);
    if (user) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', String(variant.id));
      window.history.replaceState(window.history.state, '', url);
    }

    paint();
    showImage(variant.img);
  }

  for (const group of versionGroups) {
    wireRadioGroup(group, (radio) => {
      if (radio.dataset.v) setVersion(radio.dataset.v, { user: true });
    });
  }

  // ---- Coverage -----------------------------------------------------------------------------
  const coverage = document.querySelector('[data-pv1-group="coverage"]');
  if (coverage instanceof HTMLElement) {
    wireRadioGroup(coverage, (selected) => {
      state.cover = Number(selected.dataset.w) || 0;
      state.coverLabel = selected.dataset.lbl || '';
      for (const radio of all('[role="radio"]', coverage)) {
        const on = radio === selected;
        radio.setAttribute('aria-checked', String(on));
        radio.tabIndex = on ? 0 : -1;
      }
      paint();
    });
  }

  // ---- Quantity -----------------------------------------------------------------------------
  /** @param {number} next */
  function setQuantity(next) {
    state.quantity = Math.min(MAX_QUANTITY, Math.max(1, next));
    setText('qv', String(state.quantity));
    if (quantityInput instanceof HTMLInputElement) quantityInput.value = String(state.quantity);
    paint();
  }
  hook('qm')?.addEventListener('click', () => setQuantity(state.quantity - 1));
  hook('qp')?.addEventListener('click', () => setQuantity(state.quantity + 1));

  setVersion(state.version);
}

function initAccordions() {
  for (const button of all('[data-pv1-acc]')) {
    if (!claim(button)) continue;
    button.addEventListener('click', () => {
      const panel = document.getElementById(button.getAttribute('aria-controls') || '');
      if (!panel) return;
      const open = button.getAttribute('aria-expanded') === 'true';
      button.setAttribute('aria-expanded', String(!open));
      panel.hidden = open;
    });
  }
}

/** Rails scroll by one whole card: the first visible card's width plus the rail gap. */
function initRails() {
  for (const wrap of all('[data-pv1-rails]')) {
    if (!claim(wrap)) continue;
    const rail = wrap.querySelector('[data-pv1-rail]');
    if (!(rail instanceof HTMLElement)) continue;
    const step = () => {
      const card = Array.from(rail.children).find((child) => child instanceof HTMLElement && !child.hidden);
      const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
      return card ? card.getBoundingClientRect().width + gap : rail.clientWidth;
    };
    const behavior = () => (reducedMotion() ? 'auto' : 'smooth');
    wrap.querySelector('[data-pv1-rail-prev]')?.addEventListener('click', () => rail.scrollBy({ left: -step(), behavior: behavior() }));
    wrap.querySelector('[data-pv1-rail-next]')?.addEventListener('click', () => rail.scrollBy({ left: step(), behavior: behavior() }));
  }
}

/** Creator cards: thumbnail first; the youtube-nocookie player is created only on press. */
function initCreators() {
  for (const button of all('.pv1 .ex-play')) {
    if (!claim(button)) continue;
    const card = button.closest('.ex-card');
    if (!(card instanceof HTMLElement)) continue;
    const image = button.querySelector('img');
    if (image) {
      image.addEventListener('error', () => card.classList.add('noimg'));
      if (image.complete && image.currentSrc && image.naturalWidth === 0) card.classList.add('noimg');
    }
    button.addEventListener('click', () => {
      const id = card.dataset.yt;
      const holder = button.parentElement;
      if (!id || !holder) return;
      const frame = document.createElement('iframe');
      frame.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&rel=0`;
      frame.title = card.querySelector('.ex-t')?.textContent || 'Creator video';
      frame.allow = 'accelerometer; autoplay; encrypted-media; picture-in-picture';
      frame.allowFullscreen = true;
      frame.referrerPolicy = 'strict-origin-when-cross-origin';
      holder.replaceChildren(frame);
      holder.classList.add('playing');
      frame.focus();
    });
  }
}

function initDifferences() {
  const toggle = document.querySelector('[data-pv1-diff]');
  if (!(toggle instanceof HTMLInputElement) || !claim(toggle)) return;
  toggle.addEventListener('change', () => {
    document.querySelector('[data-pv1-ctable]')?.classList.toggle('diff-only', toggle.checked);
    document.querySelector('[data-pv1-group="compare-m"]')?.classList.toggle('diff-only', toggle.checked);
  });
}

function initReviews() {
  const list = document.querySelector('[data-pv1-revlist]');
  const pager = document.querySelector('[data-pv1-pager]');
  if (!(list instanceof HTMLElement) || !(pager instanceof HTMLElement) || !claim(list)) return;

  const reviews = all('.rev', list);
  const pages = Math.max(1, ...reviews.map((review) => Number(review.dataset.page) || 1));
  if (pages < 2) return;

  const buttons = Array.from({ length: pages }, (_, i) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.textContent = String(i + 1);
    button.setAttribute('aria-label', `${pager.dataset.pageLabel || 'Page'} ${i + 1}`);
    button.addEventListener('click', () => goTo(i + 1, true));
    return button;
  });
  pager.replaceChildren(...buttons);

  /**
   * @param {number} page
   * @param {boolean} scroll
   */
  function goTo(page, scroll) {
    for (const review of reviews) review.hidden = Number(review.dataset.page) !== page;
    buttons.forEach((button, i) =>
      i + 1 === page ? button.setAttribute('aria-current', 'page') : button.removeAttribute('aria-current')
    );
    if (scroll) list.closest('section')?.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'start' });
  }
  goTo(1, false);
}

/** Sticky section bar: shown once the buy box has scrolled out above the viewport. */
function initSubnav() {
  const bar = document.querySelector('[data-pv1-subnav]');
  const buy = document.querySelector('[data-pv1-buy]');
  if (!(bar instanceof HTMLElement) || !(buy instanceof HTMLElement) || !claim(bar)) return;

  /** @param {boolean} shown */
  const setShown = (shown) => {
    bar.classList.toggle('show', shown);
    bar.inert = !shown;
    document.documentElement.classList.toggle('pv1-subnav-shown', shown);
  };
  new IntersectionObserver((entries) => {
    for (const entry of entries) setShown(!entry.isIntersecting && entry.boundingClientRect.bottom < 0);
  }).observe(buy);

  const links = all('nav a', bar);
  const targets = links.map((link) => document.getElementById((link.getAttribute('href') || '').slice(1)));
  let frame = 0;
  const spy = () => {
    frame = 0;
    let active = -1;
    targets.forEach((target, i) => {
      if (target && target.getBoundingClientRect().top <= 120) active = i;
    });
    links.forEach((link, i) => {
      link.classList.toggle('on', i === active);
      if (i === active) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
  };
  document.addEventListener(
    'scroll',
    () => {
      if (!frame) frame = requestAnimationFrame(spy);
    },
    { capture: true, passive: true }
  );
  spy();
}

/**
 * Films: decorative, muted, looped, playing only while on screen, and never started under
 * `prefers-reduced-motion`. Each film's Pause / Play control (WCAG 2.2.2) is revealed here because
 * it works only with this module. A pause chosen by the visitor holds until they press Play.
 */
function initFilms() {
  for (const video of all('video[data-pv1-film]')) {
    if (!(video instanceof HTMLVideoElement) || !claim(video)) continue;
    video.muted = true;
    let held = reducedMotion();
    let onScreen = !('IntersectionObserver' in window);
    const play = () => video.play()?.catch(() => {});
    const update = () => (onScreen && !held ? play() : video.pause());

    const toggle = video.parentElement?.querySelector('[data-pv1-film-toggle]');
    if (toggle instanceof HTMLButtonElement) {
      const sync = () => {
        const playing = !video.paused;
        toggle.dataset.state = playing ? 'playing' : 'paused';
        toggle.setAttribute('aria-label', (playing ? toggle.dataset.labelPause : toggle.dataset.labelPlay) || '');
      };
      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);
      toggle.addEventListener('click', () => {
        held = !video.paused;
        if (held) video.pause();
        else play();
      });
      sync();
      toggle.hidden = false;
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        (entries) => {
          for (const entry of entries) onScreen = entry.isIntersecting;
          update();
        },
        { threshold: 0.2 }
      ).observe(video);
    } else {
      update();
    }
  }
}

function init() {
  initBuyBox();
  initSubnav();
  initAccordions();
  initRails();
  initCreators();
  initDifferences();
  initReviews();
  initFilms();
}

init();
document.addEventListener('shopify:section:load', init);
